import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useSeo } from "../hooks/useSeo";

export default function Cases() {
  const { t, lang } = useI18n();
  useSeo({ title: t.seo.cases.title, description: t.seo.cases.description, path: "/dossiers", lang });

  return (
    <>
      <section className="container-wide pt-12 md:pt-20 pb-12 md:pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-12 flex items-center justify-between gap-6">
            <p className="eyebrow">{t.cases.eyebrow}</p>
            <p className="hidden md:block text-xs uppercase tracking-[0.22em] text-muted-foreground font-bold">¶ {t.common.benelux}</p>
          </div>
          <h1 className="lg:col-span-12 text-[40px] leading-[0.98] tracking-[-0.025em] font-medium text-foreground sm:text-[60px] lg:text-[96px] xl:text-[104px]">
            {t.cases.title}
          </h1>
          <div className="lg:col-span-8">
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">{t.cases.lead}</p>
          </div>
        </div>
      </section>

      <section className="border-t border-border" data-testid="cases-list">
        <div className="container-wide py-10 md:py-16">
          <ol className="border-y border-border">
            {t.cases.items.map((c, i) => {
              const ref = `${c.city} — ${c.type}`;
              const params = new URLSearchParams({ intent: "intake", case: ref, city: c.city, type: c.type });
              return (
                <li
                  key={i}
                  className="group grid md:grid-cols-12 gap-x-8 gap-y-3 py-8 md:py-10 border-b border-border last:border-b-0 items-baseline transition-colors hover:bg-secondary/30"
                  data-testid={`case-${i}`}
                >
                  <span className="md:col-span-1 font-display text-3xl text-accent leading-none">{String(i + 1).padStart(2, "0")}</span>

                  <div className="md:col-span-3">
                    <p className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground">{t.cases.cityLabel}</p>
                    <p className="mt-1 font-display text-2xl md:text-3xl text-foreground tracking-tight leading-snug">{c.city}</p>
                  </div>

                  <div className="md:col-span-4">
                    <p className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground">{t.cases.typeLabel}</p>
                    <p className="mt-1 text-base md:text-lg text-foreground leading-snug">{c.type}</p>
                    <p className="mt-2 text-sm text-muted-foreground">{t.cases.audienceLabel} — {c.audience}</p>
                  </div>

                  <div className="md:col-span-2">
                    <p className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground">{t.cases.durationLabel}</p>
                    <p className="mt-1 font-display text-2xl text-foreground tracking-tight">{c.duration}</p>
                  </div>

                  <div className="md:col-span-2">
                    <p className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground">{t.cases.resultLabel}</p>
                    <p className="mt-1 text-sm text-foreground/90 leading-snug">{c.result}</p>
                  </div>

                  <div className="md:col-span-12 mt-3 md:mt-2 md:pl-[8.333%]">
                    <Link
                      to={`/contact?${params.toString()}`}
                      className="inline-flex items-center gap-1.5 text-sm text-foreground/70 hover:text-accent transition-colors group/cta"
                      data-testid={`case-cta-${i}`}
                    >
                      <span className="link-underline">{t.cases.similarCta}</span>
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover/cta:-translate-y-0.5 group-hover/cta:translate-x-0.5" />
                    </Link>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      <section className="border-t border-border bg-secondary/40">
        <div className="container-wide py-16 md:py-20 grid md:grid-cols-12 gap-6 items-end">
          <p className="md:col-span-8 font-display text-2xl md:text-3xl text-foreground tracking-[-0.015em] leading-snug">
            {t.home.ctaTitle}
          </p>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contact?intent=intake" className="btn-primary" data-testid="cases-cta">
              {t.nav.bookIntake} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
